import { backendClient, transformPaginationParams } from "@/api/backendClient";
import { MaintenanceItemStatus } from "@/supabase.types";

import type {
  MaintenanceItemAction,
  MaintenanceItemActionInput,
  MaintenanceItemContract,
  MaintenanceItemInsert,
  MaintenanceItemsBulkResolve,
  MaintenanceItemUpdate,
  MaintenanceItemWithMaintenance,
} from "@waku-care/types/contracts/internal";

export const getMaintenanceItemsByMaintenanceId = async (maintenance_id: string) => {
  const { data, error } = await backendClient.get<MaintenanceItemContract[]>(
    `/maintenance-items/by-maintenance/${maintenance_id}`,
  );
  if (error) throw error;
  return data;
};

export interface GetMaintenanceItemsWithNotesByDeviceIdProps {
  device_id: string;
  page: number;
  size: number;
}
// resolved items (incl. device logs) of a device with notes, last resolved first
export const getMaintenanceItemsWithNotesByDeviceId = async ({
  device_id,
  page,
  size,
}: GetMaintenanceItemsWithNotesByDeviceIdProps): Promise<{
  data: MaintenanceItemWithMaintenance[];
  count: number | null;
}> => {
  const { data, count, error } = await backendClient.get<MaintenanceItemWithMaintenance[]>(
    `/maintenance-items/by-device/${device_id}`,
    {
      params: {
        ...transformPaginationParams(page, size),
        with_notes: true,
        statuses: [MaintenanceItemStatus.completed, MaintenanceItemStatus.skipped, MaintenanceItemStatus.failed],
      },
    },
  );
  if (error) throw error;
  return { data, count };
};

// custom item of a maintenance's device, or a device log (no maintenance) — workspace, deployment and creator are set
// by the backend
export const createMaintenanceItem = async ({ values }: { values: MaintenanceItemInsert }) => {
  const { data, error } = await backendClient.post<MaintenanceItemContract>("/maintenance-items", { body: values });
  if (error) throw error;
  return data;
};

export const updateMaintenanceItem = async ({ id, values }: { id: string; values: MaintenanceItemUpdate }) => {
  const { data, error } = await backendClient.patch<MaintenanceItemContract>(`/maintenance-items/${id}`, {
    body: values,
  });
  if (error) throw error;
  return data;
};

// pending items only
export const deleteMaintenanceItem = async (id: string) => {
  const { error } = await backendClient.delete<null>(`/maintenance-items/${id}`);
  if (error) throw error;
};

// status action of one item — the backend assigns the caller, completing applies the item's part usage, skipping /
// failing removes it
export const applyMaintenanceItemAction = async ({
  id,
  action,
  values = {},
}: {
  id: string;
  action: MaintenanceItemAction;
  values?: MaintenanceItemActionInput;
}) => {
  const { data, error } = await backendClient.post<MaintenanceItemContract>(`/maintenance-items/${id}/${action}`, {
    body: values,
  });
  if (error) throw error;
  return data;
};

// skips / fails / completes the pending and started items of a maintenance (or one of its devices)
export const bulkResolveMaintenanceItems = async (values: MaintenanceItemsBulkResolve) => {
  const { data, error } = await backendClient.post<MaintenanceItemContract[]>("/maintenance-items/bulk-resolve", {
    body: values,
  });
  if (error) throw error;
  return data;
};
